/**
 * UI-layer configuration for the Leads CRM feature (presentation only).
 */

export type LeadStage =
  | "new" | "contacted" | "trial_scheduled" | "trial_done"
  | "converted" | "lost" | "nurturing" | "on_hold";

export type LeadSource =
  | "website" | "referral" | "walk_in" | "social_media" | "other"
  | "phone_call" | "google_form" | "whatsapp" | "paid_ad" | "public_trial";

export type InterestLevel = "cold" | "warm" | "hot";

export const LEADS_LIST_PAGE_SIZE = 50;

export const DEFAULT_VISIBLE_COLUMNS = [
  "name", "phone", "stage", "interest_level",
  "assigned_to", "follow_up_date", "source"
] as const;

export const STAGE_PIPELINE_ORDER: LeadStage[] = [
  "new", "contacted", "trial_scheduled", "trial_done",
  "nurturing", "converted", "lost", "on_hold"
];

export const BULK_IMPORT_PREVIEW_ROWS = 10;

export const SOURCE_PRESETS: LeadSource[] = [
  "phone_call", "walk_in", "google_form", "whatsapp",
  "referral", "paid_ad", "website", "social_media",
  "public_trial", "other"
];

export const SOURCE_LABEL_MAP: Record<LeadSource, string> = {
  phone_call:   "Phone Call",
  walk_in:      "Walk-in",
  google_form:  "Google Form",
  whatsapp:     "WhatsApp",
  referral:     "Referral",
  paid_ad:      "Our Ads",
  website:      "Website",
  social_media: "Social Media",
  public_trial: "Public Trial Booking",
  other:        "Other",
};

export const STAGE_LABEL_MAP: Record<LeadStage, string> = {
  new:              "New",
  contacted:        "Contacted",
  trial_scheduled:  "Trial Scheduled",
  trial_done:       "Trial Done",
  nurturing:        "Nurturing",
  converted:        "Converted",
  lost:             "Lost",
  on_hold:          "On Hold",
};

export const STAGE_COLOR_MAP: Record<LeadStage, string> = {
  new:              "#6366f1",
  contacted:        "#3b82f6",
  trial_scheduled:  "#f59e0b",
  trial_done:       "#10b981",
  nurturing:        "#8b5cf6",
  converted:        "#22c55e",
  lost:             "#ef4444",
  on_hold:          "#94a3b8",
};

export const INTEREST_LEVEL_COLOR_MAP: Record<InterestLevel, string> = {
  cold: "#94a3b8",
  warm: "#f59e0b",
  hot:  "#ef4444",
};

export const INTEREST_LEVEL_LABEL_MAP: Record<InterestLevel, string> = {
  cold: "Cold",
  warm: "Warm",
  hot:  "Hot 🔥",
};

/** Legal stage transitions (mirrors backend FSM). */
export const STAGE_TRANSITIONS: Partial<Record<LeadStage, LeadStage[]>> = {
  new:              ["contacted", "on_hold", "lost"],
  contacted:        ["trial_scheduled", "nurturing", "on_hold", "lost"],
  trial_scheduled:  ["trial_done", "on_hold", "lost"],
  trial_done:       ["converted", "nurturing", "on_hold", "lost"],
  nurturing:        ["contacted", "on_hold", "lost"],
  on_hold:          [], // dynamic: previous_stage only
  converted:        [], // terminal
  lost:             [], // terminal
};

/** Stages where a "Convert to Student" button should appear. */
export const CONVERTIBLE_STAGES: LeadStage[] = ["trial_done", "contacted"];
